<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import AuthFlow from '@/components/shared/AuthFlow.vue';
import { UpdateShiftAction } from '@/interfaces';
import { ShiftFragment } from '@/graphql/custom-types';
import { useWorkScheduleStore } from '@/stores/workSchedule';
import { useDepartmentStore } from '@/stores/department';
import { addDays, subDays } from 'date-fns';
import { useInstitutionStore } from '@/stores/institution';
import { get } from 'lodash';
import ReplaceShiftFlow from '@/components/workSchedule/ReplaceShiftFlow.vue';
import { eventBus } from '@/utils/eventBus';
import { ProfileFragment } from '@/graphql/custom-types';
import { Error_Code } from '@/graphql/generated-types';
import { useI18n } from 'vue-i18n';
import toErrorCode from '@/utils/exceptions/toErrorCode';

interface Props {
  titleSuccess: string;
  title: string;
  shift: ShiftFragment;
  action: UpdateShiftAction;
}

const props = withDefaults(defineProps<Props>(), {});

const { t } = useI18n();
const workScheduleStore = useWorkScheduleStore();
const departmentStore = useDepartmentStore();
const institutionStore = useInstitutionStore();
const showAuthFlow = ref<boolean>(false);
const showReplaceFlow = ref<boolean>(false);
const authFlowLoading = ref<boolean>(false);

const emit = defineEmits(['close']);

const shownDays = computed(() => {
  // If the department has checked overwrite institution setting it will be in the shift array.
  const matchingDepartment = (institutionStore.settings?.shift || []).find(
    ({ departmentId }) =>
      departmentId?.toString() == departmentStore.id.toString()
  );

  return (
    (get(
      matchingDepartment,
      'daysForward',
      departmentStore.settings?.shift?.daysForward
    ) || 1) - 1 // Subtract 1 because the core admin setting is not 0 based but 1 should mean 1 days forward.
  );
});

const dateInterval = computed(() => {
  const today = new Date();

  return {
    from: subDays(today, shownDays.value - 1),
    to: addDays(today, shownDays.value - 1),
  };
});

// action can change on an already-mounted instance (e.g. cancel then replace), so react to it rather than only checking once on mount.
watch(
  () => props.action,
  (action) => {
    showReplaceFlow.value = action === 'replace';
    showAuthFlow.value = action !== 'replace';
  },
  { immediate: true }
);

const closeFlow = () => {
  showAuthFlow.value = false;
  showReplaceFlow.value = false;
  emit('close');
};

const cancelShift = async () => {
  await workScheduleStore.shiftUpdate({
    dates: dateInterval.value,
    cancel: true,
  });
};

const reactivateShift = async () => {
  await workScheduleStore.shiftUpdate({
    dates: dateInterval.value,
    cancel: false,
  });
};

const replaceShift = async (employee: ProfileFragment) => {
  try {
    authFlowLoading.value = true;

    workScheduleStore.setReplaceWithEmployeeId(employee.id);

    await workScheduleStore.shiftUpdate({
      dates: dateInterval.value,
      cancel: false,
    });
    eventBus.emit('success', props.titleSuccess);
  } catch (error) {
    if (toErrorCode(error) === Error_Code.Conflict) {
      eventBus.emit('error', t('employee.shift_conflict'));
    } else {
      eventBus.emit('anErrorOccurred');
    }
  } finally {
    authFlowLoading.value = false;
    showReplaceFlow.value = false;
    showAuthFlow.value = false;
    workScheduleStore.setReplaceWithEmployeeId(null);
  }
};

const deleteShift = async () => {
  await workScheduleStore.shiftDelete({
    shiftId: props.shift.id,
    dates: dateInterval.value,
  });
};

const onSuccess = async (payload: ProfileFragment) => {
  try {
    authFlowLoading.value = true;

    if (props.action === 'cancel') await cancelShift();
    if (props.action === 'reactivate') await reactivateShift();
    if (props.action === 'replace') await replaceShift(payload);
    if (props.action === 'delete') await deleteShift();
    eventBus.emit('success', props.titleSuccess);
  } catch {
    eventBus.emit('anErrorOccurred');
  } finally {
    authFlowLoading.value = false;
    showReplaceFlow.value = false;
    showAuthFlow.value = false;
    workScheduleStore.setReplaceWithEmployeeId(null);
  }
};
</script>

<template>
  <AuthFlow
    v-if="showAuthFlow"
    :title="title"
    :employee-only="true"
    :loading="authFlowLoading"
    @close="closeFlow"
    @authenticated="onSuccess"
  />

  <ReplaceShiftFlow
    v-if="showReplaceFlow"
    :title="title"
    :shift="shift"
    :loading="authFlowLoading"
    @close="closeFlow"
    @success="replaceShift"
  />
</template>
